import { useCallback, useMemo, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { DatePicker } from 'ui-components/components/date-picker';
import { SearchableDropdownField } from 'ui-components/components/dropdown';
import type { DropdownOption } from 'ui-components/components/dropdown/dropdown-types';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Input, version2 } from 'ui-components/components/input';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { GuidService } from 'ui-components/services/guid-service';

import ModalPortal from '../../../../../../components/modal-portal';
import { UserPermissionService } from '../../../../../../services/user-permission-service';
import Global from '../../../../../../store/global-selectors';
import type { AuditProduct } from '../audit-products-section/audit-products-section';
import { AuditProductsSection } from '../audit-products-section/audit-products-section';
import styles from './audit-form-modal.styles.module.less';
import { messages } from './messages';

const NOTES_MAX_LENGTH = 2000;
const NOTES_PLACEHOLDER = `0/${NOTES_MAX_LENGTH}`;
const UNASSIGNED_USER_ID = '';

const getUserOptionLabel = (option: DropdownOption) => option.name as string;
const getUserOptionValue = (option: DropdownOption) => String(option.userId);

type AssignAuditUser = {
  userId: number | string;
  name: string;
  roles?: string[];
};

export type AuditFormData = {
  auditName: string;
  dueDate: string;
  assigneeUserId: string;
  notes: string;
};

type AuditFormModalProps = {
  captionMessage?: MessageDescriptor;
  captionValues?: Record<string, unknown>;
  confirmLabel: MessageDescriptor;
  deleteLabel?: MessageDescriptor;
  initialValues?: AuditFormData;
  isCancelling?: boolean;
  isSaving?: boolean;
  onClose: () => void;
  onConfirm: (data: AuditFormData) => void;
  onDelete?: () => void;
  products?: AuditProduct[];
  titleMessage: MessageDescriptor;
};

export const AuditFormModal = ({
  captionMessage,
  captionValues,
  confirmLabel,
  deleteLabel,
  initialValues,
  isCancelling = false,
  isSaving = false,
  onClose,
  onConfirm,
  onDelete,
  products,
  titleMessage,
}: AuditFormModalProps) => {
  const intl = useIntl();
  const dateFormat = useSelector(Global.selectUserUserDateFormat) as string;
  const users = useSelector(Global.selectSellerUsersSortedByName) as AssignAuditUser[];
  const inventoryManagementUsers = useMemo(() => {
    const filteredUsers = users.filter((user) => UserPermissionService.userHasInventoryManagementRole(user.roles ?? []));
    return [{ userId: UNASSIGNED_USER_ID, name: intl.formatMessage(messages.unassignedOption) }, ...filteredUsers];
  }, [intl, users]);
  const modalHeaderId = useMemo(() => GuidService.newGuid(), []);
  const assignToDropdownId = useMemo(() => GuidService.newGuid(), []);

  const [auditName, setAuditName] = useState(initialValues?.auditName ?? '');
  const [dueDate, setDueDate] = useState(initialValues?.dueDate ?? '');
  const [assigneeUserId, setAssigneeUserId] = useState(initialValues?.assigneeUserId ?? '');

  const handleAssigneeChange = useCallback((value: string | null) => {
    setAssigneeUserId(value ?? UNASSIGNED_USER_ID);
  }, []);
  const [notes, setNotes] = useState(initialValues?.notes ?? '');

  const handleAuditNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAuditName(e.target.value);
  }, []);

  const handleConfirm = useCallback(() => {
    onConfirm({ auditName: auditName.trim(), dueDate, assigneeUserId, notes: notes.trim() });
  }, [auditName, dueDate, assigneeUserId, notes, onConfirm]);

  const isConfirmDisabled = !auditName.trim() || !dueDate;
  const hasDeleteButton = !!onDelete && !!deleteLabel;

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={styles.modal} modalHeaderId={modalHeaderId}>
        <ModalHeader closeModal={onClose} id={modalHeaderId}>
          <Text textType={TextType.H3} value={titleMessage} />
        </ModalHeader>
        <ModalBody className={styles['modal-body']}>
          <FlexColumn gap={Gap.Space16}>
            <FlexColumn gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.auditNameLabel} />
              <Input name='auditName' onChange={handleAuditNameChange} value={auditName} version={version2} />
            </FlexColumn>
            <FlexColumn gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.dueDateLabel} />
              <DatePicker
                calendarPosition='bottom-right'
                className={styles['date-picker']}
                dateFormat={dateFormat}
                onChange={setDueDate}
                placeholder=''
                shouldInputHaveFullWidth
                value={dueDate}
              />
            </FlexColumn>
            <FlexColumn gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.assignToLabel} />
              <SearchableDropdownField
                getOptionLabel={getUserOptionLabel}
                getOptionValue={getUserOptionValue}
                id={assignToDropdownId}
                isClearable
                onItemSelected={handleAssigneeChange}
                options={inventoryManagementUsers}
                placeholder={intl.formatMessage(messages.assignToPlaceholder)}
                selectedItemValue={assigneeUserId}
              />
            </FlexColumn>
            <FlexColumn gap={Gap.Space4}>
              <Text textType={TextType.Label} value={messages.notesLabel} />
              <FloatTextArea
                className={styles['notes-input-wrapper']}
                maxLength={NOTES_MAX_LENGTH}
                onChange={setNotes}
                placeholder={NOTES_PLACEHOLDER}
                value={notes}
              />
            </FlexColumn>
            {products ? <AuditProductsSection products={products} /> : null}
            {captionMessage ? (
              <Text
                className={styles['caption-box']}
                formattedMessageValues={{ b: (chunks: React.ReactNode) => <b>{chunks}</b>, ...captionValues }}
                textType={TextType.Caption}
                value={captionMessage}
              />
            ) : null}
          </FlexColumn>
        </ModalBody>
        <ModalFooter>
          {hasDeleteButton ? (
            <FlexRow className={styles['modal-footer-with-delete']} gap={Gap.Space8}>
              <Button isLoading={isCancelling} onClick={onDelete} theme={THEME_CONSTANTS.tertiary}>
                <FormattedMessage {...deleteLabel} />
              </Button>
              <FlexRow gap={Gap.Space8}>
                <ModalFooterCancelButton closeModal={onClose} />
                <Button disabled={isConfirmDisabled} isLoading={isSaving} onClick={handleConfirm} theme={THEME_CONSTANTS.primary}>
                  <FormattedMessage {...confirmLabel} />
                </Button>
              </FlexRow>
            </FlexRow>
          ) : (
            <>
              <ModalFooterCancelButton closeModal={onClose} />
              <Button disabled={isConfirmDisabled} isLoading={isSaving} onClick={handleConfirm} theme={THEME_CONSTANTS.primary}>
                <FormattedMessage {...confirmLabel} />
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
